/**
 * ============================================================
 * Product Customizer — Nested Seat Variants
 * File: assets/product-customizer.js
 *
 * SEAT LAYOUT (horizontal grid):
 *   Row 1: [①momo] [②ume] [③tsubaki] [④sakura] [⑤kikyo] [⑥kiku]  ← icons
 *   Row 2: [color1] [color1] [color1]  [color1]  [color1] [color1]  ← first color of each
 *   Row 3: [color2] [color2] [color2]  [color2]  [color2] [color2]  ← second color of each
 *
 * Colors per type come from metafield seat_types[].colors[] array.
 * This means colors always display correctly even if some variants
 * haven't been created yet in Shopify admin.
 * ============================================================
 */
(function () {
  'use strict';

  /* ── Unicode icon map ───────────────────────────────────── */
  const ICON_CHARS = {
    snowflake:  'https://cdn.shopify.com/s/files/1/0531/5805/1991/files/4.png',
    flower6:    'https://cdn.shopify.com/s/files/1/0531/5805/1991/files/5.png',
    asterisk:   'https://cdn.shopify.com/s/files/1/0531/5805/1991/files/8.png',
    bloom:      'https://cdn.shopify.com/s/files/1/0531/5805/1991/files/sakura.jpg',
    star4:      'https://cdn.shopify.com/s/files/1/0531/5805/1991/files/6.png',
    starburst:  'https://cdn.shopify.com/s/files/1/0531/5805/1991/files/7.png' 
  };

  /* ── Helpers ────────────────────────────────────────────── */
  function parseSeat(val) {
    if (!val) return { type: '', code: '' };
    const parts = val.split('-');
    return { code: parts[parts.length - 1], type: parts.slice(0, -1).join('-') };
  }

  function formatMoney(cents, fmt) {
    const n = Math.floor(cents / 100);
    if (!fmt) return '¥' + n.toLocaleString('ja-JP');
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/g,                                  n.toFixed(0))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g,                      n.toFixed(0))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g,             n.toLocaleString('ja-JP'))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, n.toLocaleString('ja-JP'));
  }

  /* ── Main init ──────────────────────────────────────────── */
  function init(root) {
    const pid  = root.dataset.productId;
    const data = window.PCData && window.PCData[pid];
    if (!data) { console.warn('[PC] No PCData for', pid); return; }

    const { variants, seatOptionIndex, legOptionIndex, seatConfig, moneyFormat } = data;
    const colorCfg = (seatConfig && seatConfig.colors)     || {};
    const typeCfg  = (seatConfig && seatConfig.seat_types) || [];

    /* DOM refs */
    const imgEl    = root.querySelector('#pc-main-image');
    const priceEl  = root.querySelector('#pc-price');
    const vidInput = root.querySelector('#pc-variant-id');
    const propLeg  = root.querySelector('#pc-prop-leg');
    const propType = root.querySelector('#pc-prop-seat-type');
    const propClr  = root.querySelector('#pc-prop-seat-color');
    const seatGrid = root.querySelector('#pc-seat-grid');
    const oosEl    = root.querySelector('#pc-oos-msg');
    const atcBtn   = root.querySelector('#pc-atc');
    const atcText  = root.querySelector('#pc-atc-text');
    const spinner  = root.querySelector('#pc-spinner');
    const form     = root.querySelector('#pc-form');

    /* State */
    let selLeg  = null;
    let selType = null;
    let selCode = null;
    let selVar  = null;

    /* ── Build variant lookup: type → code → legName → variant ── */
    const typeMap = {};
    variants.forEach(v => {
      const leg            = v.options[legOptionIndex];
      const { type, code } = parseSeat(v.options[seatOptionIndex]);
      if (!type || !code) return;
      if (!typeMap[type])        typeMap[type]       = {};
      if (!typeMap[type][code])  typeMap[type][code] = {};
      typeMap[type][code][leg]  = v;
    });

    /* ── Ordered type list (from metafield, filtered to types with variants) ── */
    const orderedTypes = typeCfg.length
      ? typeCfg.map(t => t.id).filter(id => typeMap[id])
      : Object.keys(typeMap);

    /*
     * getCodesForType — KEY FIX:
     * Read color order from metafield seat_types[].colors[] array.
     * This gives each type its own specific colors in the correct order,
     * regardless of which codes happen to exist in typeMap.
     * Falls back to typeMap keys if metafield colors not defined.
     */
    function getCodesForType(typeId) {
      const meta = typeCfg.find(t => t.id === typeId);
      if (meta && Array.isArray(meta.colors) && meta.colors.length) {
        return meta.colors; // exact list + order from metafield
      }
      return Object.keys(typeMap[typeId] || {});
    }

    /* ── Build horizontal seat grid (runs once on load) ──── */
    function buildSeatGrid() {
      if (!seatGrid) return;
      seatGrid.innerHTML = '';
      if (!orderedTypes.length) return;

      /* Row 1: icon buttons */
      const iconRow = document.createElement('div');
      iconRow.className = 'pc-seat-icon-row';

      orderedTypes.forEach(typeId => {
        const meta     = typeCfg.find(t => t.id === typeId) || { id: typeId, label: typeId, icon: '' };
        const iconChar = ICON_CHARS[meta.icon] || meta.icon || '✿';

        const btn = document.createElement('button');
        btn.type             = 'button';
        btn.className        = 'pc-seat-type-btn';
        btn.dataset.type     = typeId;
        btn.setAttribute('aria-pressed', 'false');
        btn.setAttribute('aria-label', meta.label);
        btn.innerHTML = `
        <img src="${iconChar}" alt="${meta.label}" width="80" height="80" loading="lazy">
         
          <span class="pc-seat-type-name">${meta.label}</span>
        `;
        /* Clicking icon selects first available color of that type */
        btn.addEventListener('click', () => {
          const first = seatGrid.querySelector(`.pc-color-btn[data-type="${typeId}"]:not([disabled])`);
          if (first) first.click();
        });
        iconRow.appendChild(btn);
      });
      seatGrid.appendChild(iconRow);

      /* Rows 2+: color rows (one row per color index) */
      const maxColors = Math.max(...orderedTypes.map(id => getCodesForType(id).length));

      for (let rowIdx = 0; rowIdx < maxColors; rowIdx++) {
        const colorRow = document.createElement('div');
        colorRow.className = 'pc-seat-color-row';

        orderedTypes.forEach(typeId => {
          const codes = getCodesForType(typeId);
          const code  = codes[rowIdx]; // undefined if this type has fewer colors

          const cell = document.createElement('div');
          cell.className = 'pc-seat-color-cell';

          if (code) {
            const cfg = colorCfg[code] || { hex: '#cccccc', label: code };

            const btn = document.createElement('button');
            btn.type             = 'button';
            btn.className        = 'pc-color-btn';
            btn.dataset.type     = typeId;
            btn.dataset.code     = code;
            btn.setAttribute('aria-pressed', 'false');
            btn.setAttribute('aria-label',
              `${(typeCfg.find(t => t.id === typeId) || {}).label || typeId} ${cfg.label} #${code}`
            );
            btn.innerHTML = `
            <img class="pc-color-circle" src="${cfg.image}"></span>
              <!-- span class="pc-color-circle" style="background:${cfg.hex};"></span -->
              <span class="pc-color-code">#${code}</span>
            `;
            btn.addEventListener('click', () => onColorClick(typeId, code));
            cell.appendChild(btn);
          }
          /* Empty cell preserves column alignment */
          colorRow.appendChild(cell);
        });

        seatGrid.appendChild(colorRow);
      }
    }

    /* ── Color click ────────────────────────────────────────── */
    function onColorClick(typeId, code) {
      selType = typeId;
      selCode = code;

      seatGrid.querySelectorAll('.pc-color-btn').forEach(b => {
        b.classList.remove('is-active');
        b.setAttribute('aria-pressed', 'false');
      });
      seatGrid.querySelectorAll('.pc-seat-type-btn').forEach(b => {
        b.classList.remove('is-active');
        b.setAttribute('aria-pressed', 'false');
      });

      const colorBtn = seatGrid.querySelector(`.pc-color-btn[data-type="${typeId}"][data-code="${code}"]`);
      if (colorBtn) { colorBtn.classList.add('is-active'); colorBtn.setAttribute('aria-pressed', 'true'); }

      const typeBtn = seatGrid.querySelector(`.pc-seat-type-btn[data-type="${typeId}"]`);
      if (typeBtn) { typeBtn.classList.add('is-active'); typeBtn.setAttribute('aria-pressed', 'true'); }

      resolveAndApply();
    }

    /* ── Leg click ──────────────────────────────────────────── */
    function onLegClick(legVal, btn) {
      selLeg = legVal;
      root.querySelectorAll('.pc-leg').forEach(b => {
        b.classList.toggle('is-active', b === btn);
        b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
      });
      refreshOos();
      resolveAndApply();
    }

    /* ── Refresh OOS states ─────────────────────────────────── */
    function refreshOos() {
      seatGrid.querySelectorAll('.pc-color-btn').forEach(btn => {
        const typeId = btn.dataset.type;
        const code   = btn.dataset.code;
        /* A color is OOS if no variant exists for this leg+type+code combo */
        const v   = selLeg && typeMap[typeId]?.[code]?.[selLeg];
        const oos = selLeg ? (!v || !v.available) : false;
        btn.classList.toggle('is-oos', oos);
        btn.disabled = oos;
      });
    }

    /* ── Resolve variant + update all UI ───────────────────── */
    function resolveAndApply() {
      if (!selLeg || !selType || !selCode) {
        if (atcBtn) atcBtn.disabled = true;
        return;
      }

      const v = typeMap[selType]?.[selCode]?.[selLeg] || null;
      selVar  = v;

      if (oosEl) oosEl.hidden = (v !== null);

      if (priceEl && v) priceEl.textContent = formatMoney(v.price, moneyFormat);

      if (imgEl && v?.featured_image?.src) {
        imgEl.classList.add('pc-fading');
        setTimeout(() => {
          imgEl.src = v.featured_image.src;
          imgEl.alt = v.featured_image.alt || '';
          imgEl.classList.remove('pc-fading');
        }, 180);
      }

      const ok = v && v.available;
      if (atcBtn)  atcBtn.disabled = !ok;
      if (atcText) atcText.textContent = ok ? 'カートに入れる' : '売り切れ';

      // Always keep hidden inputs in sync so ajaxCart reads correct values
      if (vidInput && v) vidInput.value = v.id;
      const meta = typeCfg.find(t => t.id === selType);
      if (propLeg)  propLeg.value  = selLeg || '';
      if (propType) propType.value = meta ? meta.label : (selType || '');
      if (propClr)  propClr.value  = selCode
        ? ('#' + selCode + ' ' + (colorCfg[selCode]?.label || '')).trim()
        : '';
    }

    /* ── Leg button bindings ────────────────────────────────── */
    root.querySelectorAll('.pc-leg').forEach(btn => {
      btn.addEventListener('click', () => onLegClick(btn.dataset.leg, btn));
    });

    /* ── Add to Cart — let theme's ajaxCart handle it ──────────
     *
     * ROOT CAUSE of missing popup:
     * theme.liquid initializes ajaxCart with:
     *   formSelector:      '.js-product-form'
     *   addToCartSelector: '.js-product-add'
     * The ajaxCart library intercepts submit on those selectors,
     * calls /cart/add.js itself, then opens the popup.
     *
     * Our form now has class="js-product-form" and button has
     * class="js-product-add" so ajaxCart intercepts naturally.
     *
     * We only need to:
     *  1. Validate selections before submit
     *  2. Set hidden inputs (variant ID, properties)
     *  3. Allow the submit to bubble up to ajaxCart
     * ──────────────────────────────────────────────────────── */
    if (form) {
      form.addEventListener('submit', function(e) {

        // Validate — stop submit and show error if incomplete
        if (!selLeg) {
          e.preventDefault();
          e.stopImmediatePropagation();
          alert('Leg & Base を選択してください。');
          return;
        }
        if (!selType) {
          e.preventDefault();
          e.stopImmediatePropagation();
          alert('Seat タイプを選択してください。');
          return;
        }
        if (!selCode) {
          e.preventDefault();
          e.stopImmediatePropagation();
          alert('Seat カラーを選択してください。');
          return;
        }
        if (!selVar || !selVar.available) {
          e.preventDefault();
          e.stopImmediatePropagation();
          alert('この組み合わせは現在ご利用いただけません。');
          return;
        }

        // All good — make sure hidden inputs are set correctly
        if (vidInput) vidInput.value = selVar.id;
        if (propLeg)  propLeg.value  = selLeg;
        if (propType) {
          const meta = typeCfg.find(t => t.id === selType);
          propType.value = meta ? meta.label : selType;
        }
        if (propClr) {
          propClr.value = selCode
            ? ('#' + selCode + ' ' + (colorCfg[selCode]?.label || '')).trim()
            : '';
        }

        // DO NOT call e.preventDefault() here —
        // let the event bubble up so ajaxCart's listener fires
        // and opens the cart popup naturally.
      });
    }

    /* ── Defaults on load ───────────────────────────────────── */
    buildSeatGrid();

    const firstLeg = root.querySelector('.pc-leg');
    if (firstLeg) onLegClick(firstLeg.dataset.leg, firstLeg);

    const firstColor = seatGrid?.querySelector('.pc-color-btn:not([disabled])');
    if (firstColor) firstColor.click();
  }

  /* ── Boot ───────────────────────────────────────────────── */
  function boot() {
    document.querySelectorAll('.pc-wrap[data-product-id]').forEach(init);
  }
  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', boot)
    : boot();

})();
